import { handleFirebaseErrors } from '@repo/ui'
import type { SettingsV1FirebaseConfig } from '@sdk/Api'
import type { ErrorMessage, Upsertable } from '@store/errors'
import { type FirebaseApp, getApps, initializeApp } from 'firebase/app'
import {
	type Auth,
	GoogleAuthProvider,
	browserLocalPersistence,
	browserPopupRedirectResolver,
	connectAuthEmulator,
	initializeAuth,
	signInWithEmailAndPassword,
	signInWithPopup,
} from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'
import { connectStorageEmulator, getStorage } from 'firebase/storage'

export function createApp(config: SettingsV1FirebaseConfig) {
	return (
		getApps().find((app) => app.name === config.authDomain) ??
		initializeApp(config, config.authDomain)
	)
}

export function createAuth(
	config: SettingsV1FirebaseConfig,
	app: FirebaseApp,
	emulatorHost?: string,
) {
	const auth = initializeAuth(app, {
		persistence: browserLocalPersistence,
		popupRedirectResolver: browserPopupRedirectResolver,
	})
	auth.useDeviceLanguage()
	const hostPort = emulatorHost.split(':')
	if (!auth.emulatorConfig && emulatorHost && config?.projectId.startsWith('demo-')) {
		connectAuthEmulator(auth, `http://localhost:${hostPort[1]}`)
	}
	return auth
}

export function createFirestore(
	config: SettingsV1FirebaseConfig,
	app: FirebaseApp,
	emulatorHost?: string,
) {
	const db = getFirestore(app)
	const hostPort = emulatorHost.split(':')
	if (emulatorHost && config.projectId.startsWith('demo-')) {
		connectFirestoreEmulator(db, 'localhost', Number(hostPort[1]))
	}
	return db
}

export function createStorage(
	config: SettingsV1FirebaseConfig,
	app: FirebaseApp,
	emulatorHost?: string,
) {
	const storage = getStorage(app)
	const hostPort = emulatorHost.split(':')
	if (emulatorHost && config.projectId.startsWith('demo-')) {
		connectStorageEmulator(storage, 'localhost', Number(hostPort[1]))
	}
	return storage
}

export async function doLogin(
	auth: Auth,
	email: string,
	password: string,
	errors?: Upsertable<ErrorMessage>,
) {
	return await signInWithEmailAndPassword(auth, email, password).catch(handleFirebaseErrors(errors))
}

const googleProvider = new GoogleAuthProvider()

export async function doGoogleLogin(auth: Auth) {
	return await signInWithPopup(auth, googleProvider)
}
